import { supabase } from "@/lib/supabase";
import {
  ALL_SYNC_JOB_KINDS,
  SYNC_JOB_GROUPS,
  type SyncJobGroupDef,
  type SyncJobKind,
} from "@/grid/data/sync-job-catalog";

export interface SyncJobLastRun {
  id: number;
  status: string;
  started_at: string;
  finished_at: string | null;
  rows_written: number | null;
  error_detail: string | null;
}

export interface SyncRegionStatus {
  label: string;
  kind: SyncJobKind;
  lastRun: SyncJobLastRun | null;
}

export interface SyncJobGroupStatus {
  id: SyncJobGroupDef["id"];
  title: string;
  description: string;
  aggregateStatus: string | null;
  regions: SyncRegionStatus[];
}

function toLastRun(row: {
  id: number | null;
  status: string | null;
  started_at: string | null;
  finished_at: string | null;
  rows_written: number | null;
  error_detail: string | null;
}): SyncJobLastRun | null {
  if (row.id == null || !row.started_at) return null;
  return {
    id: row.id,
    status: row.status ?? "unknown",
    started_at: row.started_at,
    finished_at: row.finished_at,
    rows_written: row.rows_written,
    error_detail: row.error_detail,
  };
}

/** Worst status wins for the group header dot. */
export function aggregateSyncStatus(statuses: Array<string | null | undefined>): string | null {
  const present = statuses.filter(Boolean) as string[];
  if (present.length === 0) return null;
  if (present.some((s) => s === "failed")) return "failed";
  if (present.some((s) => s === "partial")) return "partial";
  if (present.some((s) => s === "running")) return "running";
  if (present.every((s) => s === "success")) return "success";
  return present[0] ?? null;
}

export async function fetchSyncJobStatuses(): Promise<SyncJobGroupStatus[]> {
  const { data, error } = await supabase
    .from("sync_runs")
    .select("id, job_kind, status, started_at, finished_at, rows_written, error_detail")
    .in("job_kind", [...ALL_SYNC_JOB_KINDS])
    .order("started_at", { ascending: false })
    .limit(80);

  if (error) throw new Error(`sync status: ${error.message}`);

  const latestByKind = new Map<SyncJobKind, SyncJobLastRun>();
  for (const row of data ?? []) {
    const kind = row.job_kind as SyncJobKind | null;
    if (!kind || latestByKind.has(kind)) continue;
    const lastRun = toLastRun(row);
    if (lastRun) latestByKind.set(kind, lastRun);
  }

  return SYNC_JOB_GROUPS.map((group) => {
    const regions = group.regions.map((r) => ({
      label: r.label,
      kind: r.kind,
      lastRun: latestByKind.get(r.kind) ?? null,
    }));
    return {
      id: group.id,
      title: group.title,
      description: group.description,
      aggregateStatus: aggregateSyncStatus(regions.map((r) => r.lastRun?.status)),
      regions,
    };
  });
}
